import { createSlice } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'
import type { AccountId, AssetId, ChainId } from '@shapeshiftoss/caip'
import { CHAIN_NAMESPACE, fromAccountId, fromChainId, thorchainChainId } from '@shapeshiftoss/caip'
import type { ChainAdapter, thorchain, Transaction } from '@shapeshiftoss/chain-adapters'
import type { PartialRecord, UtxoAccountType } from '@shapeshiftoss/types'
import { TxStatus } from '@shapeshiftoss/unchained-client'
import orderBy from 'lodash/orderBy'
import PQueue from 'p-queue'
import { PURGE } from 'redux-persist'

import { deserializeTxIndex, getRelatedAssetIds, serializeTxIndex } from './utils'

import { getChainAdapterManager } from '@/context/PluginProvider/chainAdapterSingleton'
import { deepUpsertArray } from '@/lib/utils'
import { BASE_RTK_CREATE_API_CONFIG } from '@/state/apis/const'
import type { State } from '@/state/apis/types'
import type { Nominal } from '@/types/common'

export type TxId = Nominal<string, 'TxId'>
export type Tx = Transaction & { accountType?: UtxoAccountType }

export type TxHistoryById = {
  [k: TxId]: Tx
}

/* this is a one to many relationship of an account and asset id to tx id
 *
 * e.g. an account with a single trade of FOX to USDC will produce the following
 * three related assets
 *
 * {
 *   0xfoobaraccount: {
 *     foxAssetId: [txid] // sell asset
 *     usdcAssetId: [txid] // buy asset
 *     ethAssetId: [txid] // fee asset
 *   }
 * }
 *
 * where txid is the same txid related to all the above assets, as the
 * sell asset, buy asset, and fee asset respectively
 *
 * this allows us to O(1) select all related transactions to a given asset
 *
 * note - we persist this data structure to disk across page refresh, and wallet disconnections
 * and use the accountIds from the connected wallet to index into it
 */

type TransactionsByAccountId = Record<AccountId, Transaction[]>

export type TxIdsByAssetId = PartialRecord<AssetId, TxId[]>
export type TxIdsByAccountIdAssetId = PartialRecord<AccountId, TxIdsByAssetId>

export type TxsState = {
  byId: TxHistoryById
  byAccountIdAssetId: TxIdsByAccountIdAssetId
  ids: TxId[]
}

export type TxHistory = {
  txs: TxsState
  hydrationMeta: PartialRecord<
    AccountId,
    { isHydrated: boolean; minTxBlockTime?: number; isErrored: boolean }
  >
}

export type TxMessage = { payload: { message: Tx; accountId: AccountId } }
export type TxsMessage = {
  payload: TransactionsByAccountId
}

export const initialState: TxHistory = {
  txs: {
    byAccountIdAssetId: {},
    byId: {},
    ids: [], // sorted, newest first
  },
  hydrationMeta: {},
}

const updateOrInsertTxs = (txHistory: TxHistory, incomingTxs: Tx[], accountId: AccountId) => {
  const { txs, hydrationMeta } = txHistory

  let newTxAdded = false
  let minTxBlockTime = Infinity
  for (const tx of incomingTxs) {
    if (tx.blockTime < minTxBlockTime) minTxBlockTime = tx.blockTime

    const txIndex = serializeTxIndex(accountId, tx.txid, tx.pubkey, tx.data)

    // track if new transactions have been added to prompt (re)sort of ids
    if (newTxAdded === false && !txs.byId[txIndex]) newTxAdded = true

    // update or insert tx
    txs.byId[txIndex] = tx

    // find all the related assetIds, and keep an index of txids related to each assetId
    getRelatedAssetIds(tx).forEach(relatedAssetId =>
      deepUpsertArray(txs.byAccountIdAssetId, accountId, relatedAssetId, txIndex),
    )
  }

  // (re)sort ids by block timestamp if any new transaction were added
  if (newTxAdded) {
    const getBlockTime = ([_, tx]: [string, Tx]) => tx.blockTime
    txs.ids = orderBy(Object.entries(txs.byId), getBlockTime, 'desc').map(([txIndex, _]) => txIndex)
  }

  // update the min tx block time if required
  if (minTxBlockTime < (hydrationMeta[accountId]?.minTxBlockTime ?? Infinity)) {
    hydrationMeta[accountId] = {
      isHydrated: false,
      isErrored: false,
      minTxBlockTime,
    }
  }
}

export const txHistory = createSlice({
  name: 'txHistory',
  initialState,
  reducers: create => ({
    clear: create.reducer(() => {
      return initialState
    }),
    onMessage: create.reducer((txState, { payload }: TxMessage) => {
      updateOrInsertTxs(txState, [payload.message], payload.accountId)
    }),
    upsertTxsByAccountId: create.reducer((txState, { payload }: TxsMessage) => {
      for (const [accountId, txs] of Object.entries(payload)) {
        updateOrInsertTxs(txState, txs, accountId)
      }

      return txState
    }),
    setAccountIdHydrated: create.reducer((txState, { payload }: { payload: AccountId }) => {
      txState.hydrationMeta[payload] = {
        minTxBlockTime: txState.hydrationMeta[payload]?.minTxBlockTime,
        isHydrated: true,
        isErrored: false,
      }
    }),
    setAccountIdErrored: create.reducer((txState, { payload }: { payload: AccountId }) => {
      txState.hydrationMeta[payload] = {
        minTxBlockTime: txState.hydrationMeta[payload]?.minTxBlockTime,
        isHydrated: false,
        isErrored: true,
      }
    }),
  }),
  selectors: {
    selectTxsById: state => state.txs.byId,
    selectTxIds: state => state.txs.ids,
    selectHydrationMeta: state => state.hydrationMeta,
    selectTxIdsByAccountIdAssetId: state => state.txs.byAccountIdAssetId,
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})

const requestQueue = new PQueue({ concurrency: 2 })

export const txHistoryApi = createApi({
  ...BASE_RTK_CREATE_API_CONFIG,
  reducerPath: 'txHistoryApi',
  endpoints: build => ({
    getAllTxHistory: build.query<null, AccountId>({
      queryFn: async (accountId, { dispatch, getState }) => {
        const { chainId, account: pubkey } = fromAccountId(accountId)
        const adapter = getChainAdapterManager().get(chainId)

        if (!adapter) {
          const data = `getAllTxHistory: no adapter available for chainId ${chainId}`
          return { error: { data, status: 400 } }
        }

        const txsByAccountId = (getState() as State).txHistory.txs.byAccountIdAssetId

        const hasExistingTxHistory = Object.values(txsByAccountId[accountId] ?? {}).some(
          txIds => txIds && txIds.length > 0,
        )

        const pageSize =
          !hasExistingTxHistory && fromChainId(chainId).chainNamespace === CHAIN_NAMESPACE.Evm
            ? 100
            : 10

        const fetch = async (getTxHistoryFns: ChainAdapter<ChainId>['getTxHistory'][]) => {
          for await (const getTxHistory of getTxHistoryFns) {
            try {
              let currentCursor = ''

              do {
                const requestCursor = currentCursor

                const txsById = (getState() as State).txHistory.txs.byId

                // Passes the list of known (confirmed) Txids for the account to the adapter getHistory() fn, so we don't spam parseTx()
                // which can be heavy in terms of parsing and of XHRs. If we already have a known confirmed Tx in the store, no need to re-parse it.
                const knownTxIds = new Set<string>()
                Object.entries(txsById).forEach(([txIndex, tx]) => {
                  // Only include transactions for this specific account that are confirmed
                  // Non-confirmed Txs may have more data at confirmed time e.g transfers, and we'll absolutely want to parse those
                  if (
                    deserializeTxIndex(txIndex).accountId === accountId &&
                    tx.status === TxStatus.Confirmed
                  ) {
                    knownTxIds.add(tx.txid)
                  }
                })

                const { cursor, transactions, txIds } = await getTxHistory({
                  cursor: requestCursor,
                  pubkey,
                  pageSize,
                  requestQueue,
                  knownTxIds,
                })

                const hasTx = txIds.some(txid => knownTxIds.has(txid))

                const results: TransactionsByAccountId = { [accountId]: transactions }
                dispatch(txHistory.actions.upsertTxsByAccountId(results))

                /**
                 * We have run into a transaction that already exists in the store, stop fetching more history
                 *
                 * Two edge cases exist currently:
                 *
                 * 1) If there was an error fetching transaction history after at least one page was fetched,
                 * the user would be missing all transactions thereafter. The next time we fetch tx history,
                 * we will think we ran into the latest existing transaction in the store and stop fetching.
                 * This means we will never upsert those missing transactions until the cache is cleared and
                 * they are successfully fetched again.
                 *
                 * We should be able to use state.txHistory.hydrationMetadata[accountId].isErrored to trigger a refetch in this instance.
                 *
                 * 2) Cached pending transactions will not be updated to completed if they are older than the
                 * last page found containing cached transactions.
                 *
                 * We can either invalidate tx history and refetch all, or refetch on a per transaction basis any cached pending txs
                 */
                if (hasTx) return

                currentCursor = cursor
              } while (currentCursor)

              // Mark this account as hydrated so downstream can determine the difference between an
              // account starting part-way thru a time period and "still hydrating".
              dispatch(txHistory.actions.setAccountIdHydrated(accountId))
            } catch (err) {
              console.error(err)
              dispatch(txHistory.actions.setAccountIdErrored(accountId))
            }
          }
        }

        if (chainId === thorchainChainId) {
          // fetch transaction history for both thorchain-1 (mainnet) and thorchain-mainnet-v1 (legacy)
          await fetch([
            adapter.getTxHistory.bind(adapter),
            (adapter as thorchain.ChainAdapter).getTxHistoryV1.bind(adapter),
          ])
        } else {
          await fetch([adapter.getTxHistory.bind(adapter)])
        }
        return { data: null }
      },
    }),
  }),
})
